import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import type { DiseaseTaxonomyNode, TreeNode } from "../../types/diseaseTaxonomy.types";
import {
  buildTree,
  getVisibleRows,
  searchTree,
  getAllDescendantIds,
  getAllNodeIds,
} from "../../utils/diseaseTaxonomyTree";
import { getDiseaseDisplayName } from "../../utils/diseaseDisplayName";

interface DiseaseTreeBrowserProps {
  nodes: DiseaseTaxonomyNode[];
  selectedIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
  therapeuticArea: string;
  onClose: () => void;
  allowSingleSelection?: boolean;
}

const ROW_HEIGHT = 36;
const INDENT_PX = 22;

const TA_LABEL: Record<string, string> = {
  cardiovascular: "Cardiovascular",
  renal: "Renal",
};

const TA_ICON: Record<string, string> = {
  cardiovascular: "❤️",
  renal: "🫘",
};

const DiseaseTreeBrowser: React.FC<DiseaseTreeBrowserProps> = ({
  nodes,
  selectedIds,
  onSelectionChange,
  therapeuticArea,
  onClose,
  allowSingleSelection = true,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  const fullRoots = useMemo(() => buildTree(nodes), [nodes]);

  const fullNodeMap = useMemo(() => {
    const m = new Map<number, TreeNode>();
    const walk = (list: TreeNode[]) => {
      for (const n of list) {
        m.set(n.id, n);
        walk(n.children);
      }
    };
    walk(fullRoots);
    return m;
  }, [fullRoots]);

  // Build a set of all ancestor IDs for each node (to check if any ancestor is selected)
  const ancestorMap = useMemo(() => {
    const m = new Map<number, Set<number>>();
    for (const n of nodes) {
      const ancestors = new Set<number>();
      let pid = n.parent_id;
      while (pid != null) {
        ancestors.add(pid);
        const parent = nodes.find((x) => x.id === pid);
        pid = parent?.parent_id ?? null;
      }
      m.set(n.id, ancestors);
    }
    return m;
  }, [nodes]);

  // Check if any ancestor of a node is selected (meaning this node is implicitly selected)
  const isAncestorSelected = useCallback(
    (nodeId: number): boolean => {
      const ancestors = ancestorMap.get(nodeId);
      if (!ancestors) return false;
      for (const aid of ancestors) {
        if (selectedIds.has(aid)) return true;
      }
      return false;
    },
    [ancestorMap, selectedIds]
  );

  // When searching, filter to only matching nodes + ancestors (pruned tree)
  const { filteredRoots, filteredNodeMap, matchIds } = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      return { filteredRoots: fullRoots, filteredNodeMap: fullNodeMap, matchIds: new Set<number>() };
    }

    const sr = searchTree(searchQuery, nodes);
    const keepIds = new Set([...sr.matchIds, ...sr.expandIds]);

    const pruneTree = (treeNodes: TreeNode[]): TreeNode[] => {
      const result: TreeNode[] = [];
      for (const n of treeNodes) {
        if (!keepIds.has(n.id)) continue;
        const prunedChildren = pruneTree(n.children);
        result.push({ ...n, children: prunedChildren });
      }
      return result;
    };

    const pruned = pruneTree(fullRoots);
    const nm = new Map<number, TreeNode>();
    const walk = (list: TreeNode[]) => {
      for (const n of list) { nm.set(n.id, n); walk(n.children); }
    };
    walk(pruned);

    return { filteredRoots: pruned, filteredNodeMap: nm, matchIds: sr.matchIds };
  }, [searchQuery, nodes, fullRoots, fullNodeMap]);

  const effectiveExpanded = useMemo(() => {
    if (searchQuery.trim()) {
      return getAllNodeIds(filteredRoots);
    }
    return expandedIds;
  }, [searchQuery, filteredRoots, expandedIds]);

  const visibleRows = useMemo(
    () => getVisibleRows(filteredRoots, effectiveExpanded),
    [filteredRoots, effectiveExpanded]
  );

  const virtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 15,
  });

  const toggleExpand = useCallback((id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelect = useCallback(
    (node: TreeNode) => {
      if (node.depth === 0) return;
      if (isAncestorSelected(node.id)) return;

      if (selectedIds.has(node.id)) {
        const next = new Set(selectedIds);
        next.delete(node.id);
        onSelectionChange(next);
        return;
      }

      // Single selection mode: replace previous selection with new one
      if (allowSingleSelection) {
        onSelectionChange(new Set([node.id]));
        return;
      }

      const next = new Set(selectedIds);
      const fullNode = fullNodeMap.get(node.id);
      if (fullNode && fullNode.children.length > 0) {
        const descIds = getAllDescendantIds(fullNode);
        for (const did of descIds) {
          if (did !== node.id) next.delete(did);
        }
      }
      next.add(node.id);
      onSelectionChange(next);
    },
    [selectedIds, onSelectionChange, fullNodeMap, isAncestorSelected, allowSingleSelection]
  );

  const expandAll = useCallback(() => {
    setExpandedIds(getAllNodeIds(filteredRoots));
  }, [filteredRoots]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) return;
    setExpandedIds(new Set());
  }, [searchQuery]);

  // Checkbox state: checked if node is selected OR any ancestor is selected
  const getCheckState = useCallback(
    (nodeId: number): "checked" | "unchecked" | "indeterminate" => {
      // If this node is directly selected
      if (selectedIds.has(nodeId)) return "checked";

      // If any ancestor is selected, this node is implicitly checked
      if (isAncestorSelected(nodeId)) return "checked";

      // Check if any descendants are selected (for indeterminate state)
      const fullNode = fullNodeMap.get(nodeId);
      if (fullNode && fullNode.children.length > 0) {
        const descIds = getAllDescendantIds(fullNode);
        const hasSelectedDesc = descIds.some((id) => id !== nodeId && selectedIds.has(id));
        if (hasSelectedDesc) return "indeterminate";
      }

      return "unchecked";
    },
    [selectedIds, fullNodeMap, isAncestorSelected]
  );

  // Compute display names: only show explicitly selected nodes (parents represent their subtrees)
  const selectedDisplayNames = useMemo(() => {
    const names: string[] = [];
    for (const id of selectedIds) {
      const n = fullNodeMap.get(id);
      if (n) names.push(getDiseaseDisplayName(n.name));
    }
    return names;
  }, [selectedIds, fullNodeMap]);

  // Console.log the selection whenever it changes
  useEffect(() => {
    if (selectedDisplayNames.length > 0) {
      console.log("Diseases Selected:", selectedDisplayNames.join(", "));
    }
  }, [selectedDisplayNames]);

  const title = TA_LABEL[therapeuticArea] ?? therapeuticArea;
  const icon = TA_ICON[therapeuticArea] ?? "🏥";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-600 w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-lg">
              {icon}
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800 dark:text-white">
                {title}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {(nodes.length - 1).toLocaleString()} diseases
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-800 dark:hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700 flex flex-col gap-2">
          <input
            type="text"
            placeholder="Search diseases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
          />
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <button
                onClick={expandAll}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Collapse All
              </button>
            </div>
            {searchQuery.trim() && (
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {matchIds.size} match{matchIds.size !== 1 ? "es" : ""}
              </span>
            )}
          </div>
        </div>

        {/* Virtualized Tree */}
        <div ref={scrollRef} className="flex-1 overflow-auto min-h-0">
          {visibleRows.length === 0 && searchQuery.trim() ? (
            <div className="flex items-center justify-center h-32 text-sm text-slate-400 dark:text-slate-500">
              No diseases match &ldquo;{searchQuery.trim()}&rdquo;
            </div>
          ) : (
            <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
              {virtualizer.getVirtualItems().map((vi) => {
                const node = visibleRows[vi.index];
                const displayName = getDiseaseDisplayName(node.name);
                const hasChildren = node.children.length > 0;
                const isExpanded = effectiveExpanded.has(node.id);
                const checkState = getCheckState(node.id);
                const isMatch = matchIds.has(node.id);
                const isRoot = node.depth === 0;
                const isLocked = isAncestorSelected(node.id);
                const isSelectedParent = selectedIds.has(node.id) && hasChildren;

                return (
                  <div
                    key={node.id}
                    data-index={vi.index}
                    ref={virtualizer.measureElement}
                    className={`absolute top-0 left-0 w-full flex items-center px-3 transition-colors ${
                      isLocked
                        ? "bg-slate-100 dark:bg-slate-700/50"
                        : "hover:bg-blue-50/60 dark:hover:bg-slate-700/50"
                    }`}
                    style={{
                      height: ROW_HEIGHT,
                      transform: `translateY(${vi.start}px)`,
                    }}
                  >
                    <div
                      className="flex items-center shrink-0"
                      style={{ width: node.depth * INDENT_PX + 24 }}
                    >
                      <div style={{ width: node.depth * INDENT_PX }} />
                      {hasChildren ? (
                        <button
                          onClick={() => toggleExpand(node.id)}
                          className="w-5 h-5 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded"
                          aria-label={isExpanded ? "Collapse" : "Expand"}
                        >
                          <svg
                            className={`w-3.5 h-3.5 transition-transform duration-150 ${isExpanded ? "rotate-90" : ""}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      ) : (
                        <div className="w-5" />
                      )}
                    </div>

                    <label className={`flex items-center shrink-0 mr-2 ${isRoot ? "cursor-not-allowed opacity-50" : isLocked ? "cursor-not-allowed" : "cursor-pointer"}`}>
                      <input
                        type="checkbox"
                        checked={checkState === "checked"}
                        disabled={isRoot || isLocked}
                        ref={(el) => {
                          if (el) el.indeterminate = checkState === "indeterminate";
                        }}
                        onChange={() => {
                          const tn = filteredNodeMap.get(node.id);
                          if (tn) toggleSelect(tn);
                        }}
                        className={`w-4 h-4 rounded focus:ring-blue-500 focus:ring-offset-0 ${
                          isRoot
                            ? "cursor-not-allowed border-slate-300 dark:border-slate-500 text-blue-600"
                            : isLocked
                            ? "cursor-not-allowed opacity-60 border-slate-400 dark:border-slate-500 text-slate-500 dark:text-slate-400"
                            : "cursor-pointer border-slate-300 dark:border-slate-500 text-blue-600"
                        }`}
                      />
                    </label>

                    <span
                      className={`text-sm truncate ${
                        isMatch && searchQuery.trim()
                          ? "font-semibold text-blue-700 dark:text-blue-300"
                          : isRoot
                          ? "font-medium text-slate-500 dark:text-slate-400"
                          : isLocked
                          ? "text-slate-400 dark:text-slate-500"
                          : "text-slate-700 dark:text-slate-200"
                      }`}
                      title={
                        displayName === node.name
                          ? node.name
                          : `${displayName} — ${node.name}`
                      }
                    >
                      {displayName}
                    </span>

                    {hasChildren && (
                      <span className="ml-1.5 text-[10px] text-slate-400 dark:text-slate-500 font-medium shrink-0">
                        ({node.child_count})
                      </span>
                    )}

                    {isSelectedParent && (
                      <span
                        className="ml-2 shrink-0 p-0.5 bg-red-100 dark:bg-red-900/40 rounded"
                        title="Children are auto-selected. To select individual children, uncheck this parent first."
                      >
                        <InformationCircleIcon className="w-4 h-4 text-red-500 dark:text-red-400" />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer — show selected disease names */}
        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          {selectedDisplayNames.length > 0 ? (
            <div className="mb-2 max-h-20 overflow-y-auto p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg border border-indigo-200 dark:border-indigo-700/50">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Disease Selected:
              </span>
              <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                {selectedDisplayNames.join(", ")}
              </span>
            </div>
          ) : (
            <p className="text-xs text-slate-400 dark:text-slate-500 mb-2">
              No diseases selected
            </p>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-400">
              {selectedIds.size} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => onSelectionChange(new Set())}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={onClose}
                className="px-4 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors shadow-sm"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(DiseaseTreeBrowser);
